import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { DroppableCanvas } from '@/components/Canvas';
import { MetadataBanner } from '@/components/Canvas/MetadataBanner';
import { PatternDefs } from '@/components/Canvas/PatternDefs';
import { DeleteConfirmation } from '@/components/UI/DeleteConfirmation';
import { HelpTooltip } from '@/components/UI/HelpTooltip';
import { KeySelector, ModeToggle, BeatsSelector } from '@/components/UI/MusicalSelector';
import { HELP_CONTENT } from '@/data/help-content';
import { WhyThisPanel, BuildFromBonesPanel, CompositionToolsPanel, CritiquePanel, HarmonizePanel, MovesPalette, ShiftBar, ClipThisButton, SnippetsPanel, LearnPiecePicker } from '@/components/Panels';
import { PulseRingTempo } from '@/components/Controls';
import { Sidebar, SidebarSection, SidebarDivider, SidebarSpacer } from '@/components/Sidebar';
import { AuthModal, UserMenu } from '@/components/Auth';
import { AudioLoadingIndicator } from '@/components/Audio';
import { ToastContainer, ConfirmationDialog } from '@/components/UI';
import { useAppViewStore } from '@/store/app-view-store';
import { showConfirmation, showDestructiveConfirm } from '@/store/confirmation-store';
import { Dashboard } from '@/components/Dashboard';
import { LandingPage } from '@/components/LandingPage';

// Lazy load modals for code splitting
const KeyboardShortcutsGuide = lazy(() => import('@/components/UI/KeyboardShortcutsGuide').then(m => ({ default: m.KeyboardShortcutsGuide })));
const AnalyzeModal = lazy(() => import('@/components/Modals/AnalyzeModal').then(m => ({ default: m.AnalyzeModal })));
const MyProgressionsModal = lazy(() => import('@/components/Modals/MyProgressionsModal').then(m => ({ default: m.MyProgressionsModal })));
const RefineModal = lazy(() => import('@/components/Modals/RefineModal').then(m => ({ default: m.RefineModal })));
const SaveProgressionDialog = lazy(() => import('@/components/Modals/SaveProgressionDialog').then(m => ({ default: m.SaveProgressionDialog })));
const GenerateModal = lazy(() => import('@/components/Modals/GenerateModal').then(m => ({ default: m.GenerateModal })));
const SettingsModal = lazy(() => import('@/components/Modals/SettingsModal').then(m => ({ default: m.SettingsModal })));
// Dev-only evaluation harness — code-split out of production builds
const CuratedEvaluation = lazy(() => import('@/components/Evaluation').then(m => ({ default: m.CuratedEvaluation })));
import { useHistory } from '@/hooks/useHistory';
import { usePlayback } from '@/hooks/usePlayback';
import { useAudioEngine } from '@/hooks/useAudioEngine';
import { useAnalysisStore } from '@/store/analysis-store';
import { useAuthStore } from '@/store/auth-store';
import { useBuildFromBonesStore } from '@/store/build-from-bones-store';
import { useProgressionsStore } from '@/store/progressions-store';
import { useRefineStore } from '@/store/refine-store';
import { useVoiceLineStore } from '@/store/voice-line-store';
import { useCanvasStore } from '@/store/canvas-store';
import { useWhyThisStore } from '@/store/why-this-store';
import { MOVES, type Move, type MoveContext } from '@/lib/moves';
import { revoiceMoveResult } from '@/utils/apply-move';
import { useMovesStore } from '@/store/moves-store';
import type { GhostChordPayload } from '@/lib/ghosts';
import { downloadMusicXML } from '@/services/musicxml-exporter';
import { generateSATBVoicing } from '@/audio/VoiceLeading';
import { deconstructProgression, getChordInsight } from '@/services/api-service';
import { captureSnippet } from '@/utils/snippet-capture';
import { insertSnippet } from '@/utils/snippet-insert';
import { useSnippetsStore, clipSnippet } from '@/store/snippets-store';
import { useModalStore } from '@/store/modal-store';
import { planFromInfluence } from '@/lib/steal/apply';
import { useReviewQueueStore } from '@/store/review-queue-store';
import type { Influence } from '@/types/influence';
import type { Chord, MusicalKey, Mode, ScaleDegree, ChordQuality, ChordExtensions, Voices, SavedProgression } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { Scale } from 'tonal';
import { CANVAS_CONFIG } from '@/utils/constants';
import './App.css';

function getChordQuality(degree: ScaleDegree, mode: Mode): ChordQuality {
  if (mode === 'major') {
    const qualities: Record<ScaleDegree, ChordQuality> = {
      1: 'major', 2: 'minor', 3: 'minor', 4: 'major', 5: 'major', 6: 'minor', 7: 'diminished',
    };
    return qualities[degree];
  } else {
    const qualities: Record<ScaleDegree, ChordQuality> = {
      1: 'minor', 2: 'diminished', 3: 'major', 4: 'minor', 5: 'major', 6: 'major', 7: 'major',
    };
    return qualities[degree];
  }
}

// Create demo chords - only startBeat matters for positioning now
function createDemoChords(key: MusicalKey, mode: Mode): Chord[] {
  const chordData = [
    { scaleDegree: 1 as ScaleDegree, quality: 'major' as ChordQuality, extensions: {}, startBeat: 0 },
    { scaleDegree: 5 as ScaleDegree, quality: 'major' as ChordQuality, extensions: {}, startBeat: 4 },
    { scaleDegree: 6 as ScaleDegree, quality: 'minor' as ChordQuality, extensions: {}, startBeat: 8 },
    { scaleDegree: 4 as ScaleDegree, quality: 'major' as ChordQuality, extensions: {}, startBeat: 12 },
    { scaleDegree: 2 as ScaleDegree, quality: 'minor' as ChordQuality, extensions: {}, startBeat: 16 },
    { scaleDegree: 3 as ScaleDegree, quality: 'minor' as ChordQuality, extensions: {}, startBeat: 20 },
    { scaleDegree: 7 as ScaleDegree, quality: 'diminished' as ChordQuality, extensions: {}, startBeat: 24 },
    { scaleDegree: 6 as ScaleDegree, quality: 'major' as ChordQuality, extensions: {}, startBeat: 28, isChromatic: true, chromaticType: 'borrowed' as const },
  ];

  let previousVoicing: Voices | undefined;

  return chordData.map((data, index) => {
    // First create chord with temporary voices
    const tempChord: Chord = {
      id: `demo-chord-${index}`,
      scaleDegree: data.scaleDegree,
      quality: data.quality,
      extensions: data.extensions,
      key,
      mode,
      isChromatic: data.isChromatic || false,
      chromaticType: data.chromaticType,
      voices: { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' }, // Temporary
      startBeat: data.startBeat,
      duration: 4,
      position: { x: 0, y: 0 }, // Not used anymore - kept for type compatibility
      size: 80,
      selected: false,
      playing: false,
      source: 'user' as const,
      createdAt: new Date().toISOString(),
    };

    // Generate proper SATB voicing with voice leading
    const voices = generateSATBVoicing(tempChord, previousVoicing);
    previousVoicing = voices;

    return { ...tempChord, voices };
  });
}

function App() {
  const [currentKey, setCurrentKey] = useState<MusicalKey>('C');
  const [currentMode, setCurrentMode] = useState<Mode>('major');
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [zoom, setZoom] = useState(1.0);
  const [selectedChordIds, setSelectedChordIds] = useState<string[]>([]);
  const [chords, setChords] = useState<Chord[]>(() => createDemoChords('C', 'major'));
  const [showVoiceLanes, setShowVoiceLanes] = useState(false);
  const [pieceTitle, setPieceTitle] = useState('Untitled');

  // View state
  const currentView = useAppViewStore((s) => s.currentView);
  const navigateToDashboard = useAppViewStore((s) => s.navigateToDashboard);


  const { pushState, undo, redo } = useHistory();
  useAudioEngine(); // Hook needed for auto-init on first interaction

  // Auth state
  const { user, initialize: initializeAuth } = useAuthStore();
  const { migrateLocalData, loadProgressions, saveProgression } = useProgressionsStore();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  // Settings and the keyboard-shortcuts guide live in modal-store, not local
  // state, so the store's single-active-modal exclusivity + shared Escape
  // handler cover them (b205ddf4 — they used to stack).
  const isSettingsModalOpen = useModalStore((s) => s.isOpen('settings'));
  const isShortcutsGuideOpen = useModalStore((s) => s.isOpen('keyboardShortcuts'));

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);


  // Migrate local data when user signs in
  useEffect(() => {
    if (user) {
      migrateLocalData(user.id).then(({ migrated }) => {
        if (migrated > 0) {
          console.log(`Migrated ${migrated} progressions to cloud`);
        }
        loadProgressions();
      });
    }
  }, [user, migrateLocalData, loadProgressions]);

  // Listen for loadProgression events from MyProgressionsModal
  useEffect(() => {
    const handleLoadProgression = (e: CustomEvent<SavedProgression>) => {
      const progression = e.detail;
      setChords(progression.chords);
      setCurrentKey(progression.key as MusicalKey);
      setCurrentMode(progression.mode as Mode);
      setPieceTitle(progression.title);
      setSelectedChordIds([]);
      // Convert time signature to beats per measure
      const beatsMap: Record<string, number> = { '2/2': 2, '3/4': 3, '4/4': 4, '6/8': 6 };
      setBeatsPerMeasure(beatsMap[progression.timeSignature] || 4);
    };

    window.addEventListener('loadProgression', handleLoadProgression as EventListener);
    return () => window.removeEventListener('loadProgression', handleLoadProgression as EventListener);
  }, []);

  const openAnalyzeModal = useAnalysisStore(state => state.openModal);

  // Listen for openAnalyzeModal events from Dashboard
  useEffect(() => {
    const handleOpenAnalyze = () => openAnalyzeModal();
    window.addEventListener('openAnalyzeModal', handleOpenAnalyze);
    return () => window.removeEventListener('openAnalyzeModal', handleOpenAnalyze);
  }, [openAnalyzeModal]);
  const metadata = useAnalysisStore(state => state.metadata);
  const convertedChords = useAnalysisStore(state => state.convertedChords);
  const phraseBoundaries = useAnalysisStore(state => state.phraseBoundaries);
  const analysisResult = useAnalysisStore(state => state.result);
  const clearAnalyzedProgression = useAnalysisStore(state => state.clearAnalyzedProgression);
  const openBuildFromBonesPanel = useBuildFromBonesStore(state => state.openPanel);
  const setBuildFromBonesLoading = useBuildFromBonesStore(state => state.setLoading);
  const setBuildFromBonesError = useBuildFromBonesStore(state => state.setError);
  const openProgressionsModal = useProgressionsStore(state => state.openModal);
  const openRefineModal = useRefineStore(state => state.openModal);

  // Voice lines
  const initializeVoiceLines = useVoiceLineStore(state => state.initializeFromChords);
  const voiceLines = useVoiceLineStore(state => state.voiceLines);


  // Sync voice lines when chords change and voice lanes are visible
  useEffect(() => {
    if (showVoiceLanes && chords.length > 0) {
      initializeVoiceLines(chords);
    }
  }, [chords, showVoiceLanes, initializeVoiceLines]);

  // Suspension connectors for playback (GRE-113)
  const suspensionConnectors = useCanvasStore(state => state.suspensionConnectors);

  // Calculate total beats based on chord positions + buffer
  const totalBeats = useMemo(() => {
    if (chords.length === 0) return 16; // Minimum empty timeline
    const maxBeat = Math.max(...chords.map(c => c.startBeat + c.duration));
    return Math.max(16, maxBeat + 8); // Add 8 beats buffer after last chord
  }, [chords]);

  const { isPlaying, playheadPosition, togglePlay, stop, tempo, setTempo, seek, seekToStart, seekToEnd, playChordIsolated, playSelectedChords } = usePlayback(chords, totalBeats, {
    voiceLinesActive: showVoiceLanes,
    voiceLines: voiceLines,
    suspensionConnectors: suspensionConnectors, // GRE-113: Pass suspensions for playback
  });

  // GRE-117: Smart play toggle - plays selection if any, otherwise all chords
  const handlePlayToggle = useCallback(() => {
    if (isPlaying) {
      stop();
    } else if (selectedChordIds.length > 0) {
      // Play only selected chords
      const selectedChords = chords.filter(c => selectedChordIds.includes(c.id));
      playSelectedChords(selectedChords);
    } else {
      togglePlay();
    }
  }, [isPlaying, stop, selectedChordIds, chords, playSelectedChords, togglePlay]);

  // Handle deep insight for selected chord(s)
  const handleDeepInsight = useCallback(async () => {
    if (selectedChordIds.length === 0) return;

    const selectedChords = chords.filter(c => selectedChordIds.includes(c.id));
    const selectedIndices = chords
      .map((c, i) => selectedChordIds.includes(c.id) ? i : -1)
      .filter(i => i !== -1);

    // Helper function to get root from scale degree
    const getRootFromDegree = (degree: ScaleDegree, key: MusicalKey, mode: Mode): string => {
      const scaleName = mode === 'major' ? 'major' : 'minor';
      const scaleNotes = Scale.get(`${key} ${scaleName}`).notes;
      if (!scaleNotes || scaleNotes.length === 0) return key;
      const noteIndex = degree - 1;
      if (noteIndex < 0 || noteIndex >= scaleNotes.length) return key;
      return scaleNotes[noteIndex];
    };

    // Convert chords to SimpleChord format for API
    const simpleChords = chords.map(c => ({
      root: getRootFromDegree(c.scaleDegree, c.key, c.mode),
      quality: c.quality,
      extensions: (c.extensions || {}) as Record<string, boolean>,
    }));

    // Open the WhyThis panel and set loading state
    const whyThisStore = useWhyThisStore.getState();
    if (selectedChords.length > 0) {
      whyThisStore.openPanel(
        selectedChords[0],
        selectedChords.length > 1 ? undefined : chords[selectedIndices[0] - 1],
        selectedChords.length > 1 ? undefined : chords[selectedIndices[0] + 1],
        chords,
        metadata ? { title: metadata.title, composer: metadata.composer } : undefined
      );

      try {
        const response = await getChordInsight(
          simpleChords,
          selectedIndices,
          currentKey,
          currentMode,
          metadata?.title,
          metadata?.composer
        );

        if (response.success && response.insight) {
          // Set the explanation in the store - map API response to ChordExplanation format
          whyThisStore.setExplanation({
            contextual: response.insight,
            technical: response.harmonicFunction || response.insight,
            historical: '',
            evolutionSteps: response.suggestions?.map((suggestion, idx) => ({
              name: `Step ${idx + 1}`,
              description: suggestion,
              chord: selectedChords[0],
            })) || [],
          });
        } else if (response.error) {
          whyThisStore.setError(response.error);
        }
      } catch (error) {
        console.error('Error getting chord insight:', error);
        whyThisStore.setError(`Failed to get insight: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }, [selectedChordIds, chords, currentKey, currentMode, metadata]);

  // Handle MusicXML export
  const handleExportMusicXML = useCallback(() => {
    // Create a progression object for export
    const timeSignatureMap: Record<number, SavedProgression['timeSignature']> = {
      2: '2/2',
      3: '3/4',
      4: '4/4',
      6: '6/8',
    };
    const progression: SavedProgression = {
      id: uuidv4(),
      title: metadata?.title || 'Untitled Progression',
      key: currentKey,
      mode: currentMode,
      tempo,
      timeSignature: timeSignatureMap[beatsPerMeasure] || '4/4',
      chords,
      tags: [],
      isFavorite: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // Get voice lines from store if initialized
    const voiceLineStore = useVoiceLineStore.getState();
    downloadMusicXML(progression, voiceLineStore.isInitialized ? voiceLineStore.voiceLines : undefined);
  }, [chords, currentKey, currentMode, tempo, beatsPerMeasure, metadata]);

  useEffect(() => {
    pushState(chords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally run only on mount to initialize history

  const saveToHistory = useCallback((newChords: Chord[]) => {
    pushState(newChords);
  }, [pushState]);

  // Load converted chords from analysis onto canvas
  useEffect(() => {
    if (convertedChords && convertedChords.length > 0 && analysisResult) {
      // Replace current chords with analyzed chords
      setChords(convertedChords);

      // Update key and mode from analysis result
      if (analysisResult.key) {
        setCurrentKey(analysisResult.key as MusicalKey);
      }
      if (analysisResult.mode) {
        setCurrentMode(analysisResult.mode as Mode);
      }
      // Update tempo from analysis result
      if (analysisResult.tempo) {
        setTempo(Math.round(analysisResult.tempo));
      }
    }
  }, [convertedChords, analysisResult, setTempo]);

  // Update all chords when key or mode changes
  useEffect(() => {
    if (chords.length === 0) return;

    // Check if any chord needs updating
    const needsUpdate = chords.some(c => c.key !== currentKey || c.mode !== currentMode);
    if (!needsUpdate) return;

    // Sort chords by position for proper voice leading
    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    let previousVoicing: Voices | undefined;

    const updatedChords = sortedChords.map(chord => {
      const newQuality = getChordQuality(chord.scaleDegree, currentMode);
      const updatedChord: Chord = {
        ...chord,
        key: currentKey,
        mode: currentMode,
        quality: newQuality,
      };
      // Regenerate voicing with new key/mode
      const voices = generateSATBVoicing(updatedChord, previousVoicing);
      previousVoicing = voices;
      return { ...updatedChord, voices };
    });

    setChords(updatedChords);
    pushState(updatedChords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, currentMode]); // Only run when key or mode changes


  // Voice line undo/redo from store
  const voiceLineUndo = useVoiceLineStore((state) => state.undo);
  const voiceLineRedo = useVoiceLineStore((state) => state.redo);
  const canUndoVoiceLines = useVoiceLineStore((state) => state.canUndo);
  const canRedoVoiceLines = useVoiceLineStore((state) => state.canRedo);

  const handleUndo = useCallback(() => {
    // If voice lanes are visible and there's voice line history, undo voice lines first
    if (showVoiceLanes && canUndoVoiceLines()) {
      voiceLineUndo();
      return;
    }
    // Otherwise undo chords
    const previousState = undo();
    if (previousState) {
      setChords(previousState);
      setSelectedChordIds([]);
    }
  }, [undo, showVoiceLanes, canUndoVoiceLines, voiceLineUndo]);

  const handleRedo = useCallback(() => {
    // If voice lanes are visible and there's voice line redo history, redo voice lines first
    if (showVoiceLanes && canRedoVoiceLines()) {
      voiceLineRedo();
      return;
    }
    // Otherwise redo chords
    const nextState = redo();
    if (nextState) {
      setChords(nextState);
      setSelectedChordIds([]);
    }
  }, [redo, showVoiceLanes, canRedoVoiceLines, voiceLineRedo]);

  // Selection handlers
  const handleSelectChord = useCallback((id: string) => {
    setSelectedChordIds([id]);
  }, []);

  const handleSelectChords = useCallback((ids: string[]) => {
    setSelectedChordIds(ids);
  }, []);

  const handleToggleChordSelection = useCallback((id: string) => {
    setSelectedChordIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  }, []);

  const handleSelectRange = useCallback((fromId: string, toId: string) => {
    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const fromIndex = sortedChords.findIndex((c) => c.id === fromId);
    const toIndex = sortedChords.findIndex((c) => c.id === toId);

    if (fromIndex === -1 || toIndex === -1) return;

    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    const rangeIds = sortedChords.slice(start, end + 1).map((c) => c.id);

    setSelectedChordIds(rangeIds);
  }, [chords]);

  const handleClearSelection = useCallback(() => {
    setSelectedChordIds([]);
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedChordIds(chords.map((c) => c.id));
  }, [chords]);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const performDelete = useCallback(() => {
    const newChords = chords.filter((c) => !selectedChordIds.includes(c.id));
    setChords(newChords);
    saveToHistory(newChords);
    setSelectedChordIds([]);
    setShowDeleteConfirm(false);
  }, [selectedChordIds, chords, saveToHistory]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedChordIds.length === 0) return;
    if (selectedChordIds.length >= 5) {
      setShowDeleteConfirm(true);
    } else {
      performDelete();
    }
  }, [selectedChordIds, performDelete]);

  const handleDuplicateSelected = useCallback(() => {
    if (selectedChordIds.length === 0) return;

    const chordsToDuplicate = chords.filter((c) => selectedChordIds.includes(c.id));
    const duplicates = chordsToDuplicate.map((chord) => ({
      ...chord,
      id: uuidv4(),
      startBeat: chord.startBeat + 4,
      createdAt: new Date().toISOString(),
    }));

    const newChords = [...chords, ...duplicates];
    setChords(newChords);
    saveToHistory(newChords);
    setSelectedChordIds(duplicates.map((c) => c.id));
  }, [selectedChordIds, chords, saveToHistory]);

  const handleSave = useCallback(() => {
    if (chords.length > 0) {
      setShowSaveDialog(true);
    }
  }, [chords.length]);

  // Helper to convert scale degree to root note for API communication
  const scaleDegreeToRoot = useCallback((scaleDegree: number, key: string): string => {
    const chromaticScale = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const keyToSemitone: Record<string, number> = {
      'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
      'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
      'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
    };
    const keySemitone = keyToSemitone[key] ?? 0;
    const majorScaleIntervals = [0, 2, 4, 5, 7, 9, 11];
    const noteIndex = (keySemitone + majorScaleIntervals[scaleDegree - 1]) % 12;
    return chromaticScale[noteIndex];
  }, []);

  // Handle Build From Bones - calls backend API for AI-generated step explanations
  const handleBuildFromBones = useCallback(async () => {
    if (chords.length === 0) {
      setBuildFromBonesError('No chords to deconstruct. Add some chords first.');
      return;
    }

    // Set loading state and open panel to show spinner
    setBuildFromBonesLoading(true);
    openBuildFromBonesPanel([]);

    try {
      // Convert chords to API format (SimpleChord)
      const simpleChords = chords.map(c => ({
        root: scaleDegreeToRoot(c.scaleDegree, c.key),
        quality: c.quality,
        extensions: c.extensions ? { ...c.extensions } as Record<string, boolean> : undefined,
      }));

      // Get song context from analysis metadata if available
      const songTitle = metadata?.title;
      const composer = metadata?.composer;

      // Call the backend API
      const response = await deconstructProgression(
        simpleChords,
        currentKey,
        currentMode,
        songTitle,
        composer
      );

      if (!response.success || !response.steps) {
        throw new Error(response.error || 'Deconstruction failed');
      }

      // Convert API response steps to include full chord data with IDs
      const stepsWithFullChords = response.steps.map(step => ({
        ...step,
        chords: step.chords.map((simpleChord, idx) => {
          // Match with original chord to preserve IDs and other data
          const originalChord = chords[idx];
          return {
            ...originalChord,
            quality: simpleChord.quality as ChordQuality,
            extensions: (simpleChord.extensions || {}) as ChordExtensions,
          };
        }),
      }));

      openBuildFromBonesPanel(stepsWithFullChords);
    } catch (error) {
      console.error('Build From Bones error:', error);
      setBuildFromBonesError(
        error instanceof Error ? error.message : 'Failed to deconstruct progression'
      );
    } finally {
      setBuildFromBonesLoading(false);
    }
  }, [chords, currentKey, currentMode, metadata, scaleDegreeToRoot, openBuildFromBonesPanel, setBuildFromBonesLoading, setBuildFromBonesError]);

  // Harmonize panel (store deleted; disabled stub uses local state)
  const [harmonizeIsOpen, setHarmonizeIsOpen] = useState(false);
  const closeHarmonizePanel = useCallback(() => setHarmonizeIsOpen(false), []);

  // Handle applying harmonized chords to canvas
  const handleApplyHarmonization = useCallback((harmonizedChords: Chord[]) => {
    setChords(harmonizedChords);
    saveToHistory(harmonizedChords);
    setSelectedChordIds([]);
  }, [saveToHistory]);

  const chordsWithPlayState = useMemo(() => {
    return chords.map(chord => ({
      ...chord,
      playing: isPlaying && playheadPosition >= chord.startBeat && playheadPosition < chord.startBeat + chord.duration,
    }));
  }, [chords, isPlaying, playheadPosition]);

  // Create current progression for saving
  const currentProgression = useMemo((): SavedProgression => {
    // Map beats per measure to time signature
    const timeSignatureMap: Record<number, SavedProgression['timeSignature']> = {
      2: '2/2',
      3: '3/4',
      4: '4/4',
      6: '6/8',
    };
    const timeSignature = timeSignatureMap[beatsPerMeasure] || '4/4';

    return {
      id: uuidv4(),
      title: pieceTitle || 'Untitled',
      key: currentKey,
      mode: currentMode,
      tempo,
      timeSignature,
      chords,
      tags: [],
      isFavorite: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      analyzedFrom: metadata ? {
        source: metadata.source,
        title: metadata.title,
        url: metadata.sourceUrl,
        // Stamped so a stored progression records which analyzer produced it
        // (81ffbb11). Rides in the existing analyzed_from JSON — no migration.
        analyzerVersion: metadata.analyzerVersion,
      } : undefined,
    };
  }, [currentKey, currentMode, tempo, beatsPerMeasure, chords, metadata, pieceTitle]);

  // Add chord - now just needs x position to calculate startBeat
  const handleAddChord = useCallback((
    scaleDegree: ScaleDegree,
    position: { x: number },
    options?: { quality?: ChordQuality; extensions?: ChordExtensions }
  ) => {
    const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
    const startBeat = Math.max(0, Math.round(position.x / beatWidth));

    const newChord: Chord = {
      id: uuidv4(),
      scaleDegree,
      quality: options?.quality || getChordQuality(scaleDegree, currentMode),
      extensions: options?.extensions || {},
      key: currentKey,
      mode: currentMode,
      isChromatic: false,
      voices: { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' }, // Temporary
      startBeat,
      duration: 4,
      position: { x: 0, y: 0 }, // Not used
      size: 80,
      selected: false,
      playing: false,
      source: 'user',
      createdAt: new Date().toISOString(),
    };

    // Find previous chord for voice leading
    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const previousChord = sortedChords.filter(c => c.startBeat < startBeat).pop();

    // Generate proper SATB voicing
    const voices = generateSATBVoicing(newChord, previousChord?.voices);
    const chordWithVoices = { ...newChord, voices };

    const newChords = [...chords, chordWithVoices];
    setChords(newChords);
    saveToHistory(newChords);
  }, [zoom, currentKey, currentMode, chords, saveToHistory]);

  // Update chord position - now just updates startBeat
  const handleUpdateChordPosition = useCallback((chordId: string, startBeat: number) => {
    setChords((prevChords) =>
      prevChords.map((chord) =>
        chord.id === chordId ? { ...chord, startBeat } : chord
      )
    );
  }, []);

  // Update chord voices - for manual voice line editing
  const handleUpdateChordVoices = useCallback((chordId: string, voices: Voices) => {
    setChords((prevChords) =>
      prevChords.map((chord) =>
        chord.id === chordId ? { ...chord, voices } : chord
      )
    );
  }, []);

  // Update chord properties (quality, extensions, etc.)
  // Regenerates voices when quality or extensions change to update audio
  const handleUpdateChord = useCallback((chordId: string, updates: Partial<Chord>) => {
    setChords((prevChords) => {
      // Check if we need to regenerate voices (quality or extensions changed)
      const needsVoiceRegeneration = 'quality' in updates || 'extensions' in updates;

      // Find the chord index and get previous chord for voice leading
      const chordIndex = prevChords.findIndex(c => c.id === chordId);
      if (chordIndex === -1) return prevChords;

      // Sort by startBeat to find the actual previous chord
      const sortedChords = [...prevChords].sort((a, b) => a.startBeat - b.startBeat);
      const sortedIndex = sortedChords.findIndex(c => c.id === chordId);
      const previousChord = sortedIndex > 0 ? sortedChords[sortedIndex - 1] : undefined;

      return prevChords.map((chord) => {
        if (chord.id !== chordId) return chord;

        const updatedChord = { ...chord, ...updates };

        // Regenerate voices if quality or extensions changed
        if (needsVoiceRegeneration) {
          updatedChord.voices = generateSATBVoicing(updatedChord, previousChord?.voices);
        }

        return updatedChord;
      });
    });
  }, []);

  // ── Moves engine integration (Phase 3/4) ─────────────────────────────────
  const clearLastApplied = useMovesStore((s) => s.clearLastApplied);

  // The selected-chord context for the moves palette: beat-ordered chords, and
  // the index of the selected chord within that order. Null unless exactly one
  // chord is selected (the palette is hidden otherwise).
  const moveContext = useMemo<MoveContext | null>(() => {
    if (selectedChordIds.length !== 1) return null;
    const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const index = sorted.findIndex((c) => c.id === selectedChordIds[0]);
    if (index === -1) return null;
    return { chords: sorted, index, key: currentKey, mode: currentMode };
  }, [chords, selectedChordIds, currentKey, currentMode]);

  // Clear the explanation strip whenever the selection changes.
  useEffect(() => {
    clearLastApplied();
  }, [selectedChordIds, clearLastApplied]);

  // Core apply: engine -> revoice changed ids -> commit -> history -> strip.
  const applyMoveAtIndex = useCallback(
    (move: Move, sortedChords: Chord[], index: number): boolean => {
      const ctx: MoveContext = { chords: sortedChords, index, key: currentKey, mode: currentMode };
      if (!move.applicable(ctx)) return false;
      const result = move.apply(ctx);
      const revoiced = revoiceMoveResult(result, zoom);
      setChords(revoiced);
      saveToHistory(revoiced);
      useMovesStore.getState().setLastApplied({
        moveId: move.id,
        explanation: result.explanation,
        theoryLabel: result.theoryLabel,
        affectedIds: result.changedIds,
        beforeChords: sortedChords,
      });
      return true;
    },
    [currentKey, currentMode, zoom, saveToHistory]
  );

  // Palette: apply a move to the single selected chord.
  const handleApplyMove = useCallback(
    (move: Move) => {
      if (!moveContext) return;
      applyMoveAtIndex(move, moveContext.chords, moveContext.index);
    },
    [moveContext, applyMoveAtIndex]
  );

  // Shift review queue: apply one kept step. Guarded by applicable() so a step
  // that no longer fits (an earlier keep shifted the context) is reported
  // 'not-applicable' rather than throwing.
  const handleApplyShiftStep = useCallback(
    (moveId: string, index: number): 'applied' | 'not-applicable' => {
      const move = MOVES.find((m) => m.id === moveId);
      if (!move) return 'not-applicable';
      const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);
      if (index < 0 || index >= sorted.length) return 'not-applicable';
      return applyMoveAtIndex(move, sorted, index) ? 'applied' : 'not-applicable';
    },
    [chords, applyMoveAtIndex]
  );

  // Hear the affected region — the current (new) way, or the pre-move (old) way.
  // The "old way" plays an uncommitted snapshot array, so it never mutates state.
  const hearRegion = useCallback(
    (which: 'new' | 'old') => {
      const last = useMovesStore.getState().lastApplied;
      if (!last) return;
      const affected = chords.filter((c) => last.affectedIds.includes(c.id));
      if (affected.length === 0) return;
      const startBeat = Math.min(...affected.map((c) => c.startBeat));
      const endBeat = Math.max(...affected.map((c) => c.startBeat + c.duration));
      const source = which === 'new' ? chords : last.beforeChords;
      const region = [...source]
        .filter((c) => c.startBeat < endBeat && c.startBeat + c.duration > startBeat)
        .sort((a, b) => a.startBeat - b.startBeat);
      if (region.length > 0) playSelectedChords(region);
    },
    [chords, playSelectedChords]
  );

  // ── Ghost suggestions (Phase 4) ──────────────────────────────────────────
  const buildGhostChord = useCallback(
    (payload: GhostChordPayload, startBeat: number, previousVoicing?: Voices): Chord => {
      const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
      const base: Chord = {
        id: uuidv4(),
        scaleDegree: payload.scaleDegree,
        quality: payload.quality,
        extensions: payload.extensions,
        key: currentKey,
        mode: currentMode,
        isChromatic: payload.isChromatic ?? false,
        chromaticType: payload.chromaticType,
        voices: { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' },
        startBeat,
        duration: 4,
        position: { x: startBeat * beatWidth, y: 0 },
        size: 80,
        selected: false,
        playing: false,
        source: 'user',
        createdAt: new Date().toISOString(),
      };
      return { ...base, voices: generateSATBVoicing(base, previousVoicing) };
    },
    [zoom, currentKey, currentMode]
  );

  const nextBeat = useCallback(() => {
    if (chords.length === 0) return 0;
    return Math.max(...chords.map((c) => c.startBeat + c.duration));
  }, [chords]);

  // Hover a ghost: voice it from the last chord and play it once, no commit.
  const handleAuditionGhost = useCallback(
    (payload: GhostChordPayload) => {
      const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);
      const previousVoicing = sorted.length > 0 ? sorted[sorted.length - 1].voices : undefined;
      playChordIsolated(buildGhostChord(payload, nextBeat(), previousVoicing));
    },
    [chords, buildGhostChord, nextBeat, playChordIsolated]
  );

  // Click a ghost: append it at the next beat with the default duration.
  const handleCommitGhost = useCallback(
    (payload: GhostChordPayload) => {
      const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);
      const previousVoicing = sorted.length > 0 ? sorted[sorted.length - 1].voices : undefined;
      const newChords = [...chords, buildGhostChord(payload, nextBeat(), previousVoicing)];
      setChords(newChords);
      saveToHistory(newChords);
    },
    [chords, buildGhostChord, nextBeat, saveToHistory]
  );

  // ── Snippets (Phase 5) ────────────────────────────────────────────────
  // "Clip this": capture the selected passage (beat span, not a scatter —
  // every chord inside the span is captured, see captureSnippet) and save
  // it to the clips library.
  const handleClipThis = useCallback(() => {
    if (selectedChordIds.length < 2) return;
    const selected = chords.filter((c) => selectedChordIds.includes(c.id));
    const snippet = captureSnippet(chords, selected, {
      key: currentKey,
      mode: currentMode,
      sourceName: metadata?.title || (pieceTitle !== 'Untitled' ? pieceTitle : undefined),
    });
    clipSnippet(snippet);
  }, [selectedChordIds, chords, currentKey, currentMode, metadata, pieceTitle]);

  // Drag a snippet from the library onto the canvas: transpose into the
  // CURRENT key/mode and revoice, then commit + push history.
  const handleInsertSnippet = useCallback(
    (snippetId: string, dropBeat: number) => {
      const snippet = useSnippetsStore.getState().snippets.find((s) => s.id === snippetId);
      if (!snippet) return;
      const newChords = insertSnippet(chords, snippet, dropBeat, currentKey, currentMode, zoom);
      setChords(newChords);
      saveToHistory(newChords);
    },
    [chords, currentKey, currentMode, zoom, saveToHistory]
  );

  // "add to page" — the non-drag, keyboard-reachable insertion path: same
  // insertSnippet flow, appended right after the last chord (nextBeat, also
  // used by the ghost-commit flow above).
  const handleAppendSnippet = useCallback(
    (snippetId: string) => {
      handleInsertSnippet(snippetId, nextBeat());
    },
    [handleInsertSnippet, nextBeat]
  );

  // ── Steal from Masters (Phase 6) ─────────────────────────────────────
  // "voice it like {name}": deterministic plan generation, fed into the SAME
  // review queue ShiftBar's AI-backed /api/shift path uses — no new apply
  // machinery, just a second producer for review-queue-store.
  const handleVoiceItLike = useCallback(
    (influence: Influence) => {
      const plan = planFromInfluence(influence, { chords, key: currentKey, mode: currentMode });
      const summary =
        plan.length > 0
          ? `voicing it like ${influence.name}`
          : `nothing of ${influence.name}'s habits fits this progression yet`;
      useReviewQueueStore.getState().loadPlan(plan, summary);
    },
    [chords, currentKey, currentMode]
  );

  // Handle new piece - reset canvas and state
  const handleNewPiece = useCallback(() => {
    if (chords.length > 0) {
      showConfirmation({
        title: 'Start New Piece',
        message: 'Any unsaved changes will be lost. Continue?',
        confirmLabel: 'Start New',
        onConfirm: () => {
          setChords([]);
          setSelectedChordIds([]);
          setCurrentKey('C');
          setCurrentMode('major');
          setBeatsPerMeasure(4);
          setShowVoiceLanes(false);
          setPieceTitle('Untitled');
          clearAnalyzedProgression();
          useVoiceLineStore.getState().initializeFromChords([]);
        },
      });
    } else {
      setPieceTitle('Untitled');
    }
  }, [chords.length, clearAnalyzedProgression]);

  // Handle back to dashboard - check for unsaved changes
  const handleBackToDashboard = useCallback(() => {
    if (chords.length > 0) {
      showDestructiveConfirm(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
        () => {
          // Clear state and navigate
          setChords([]);
          setSelectedChordIds([]);
          navigateToDashboard();
        },
        () => {
          // Cancel - do nothing
        },
        'Discard'
      );
    } else {
      navigateToDashboard();
    }
  }, [chords.length, navigateToDashboard]);

  // Dev-only: ?curated URL parameter mounts the evaluation harness.
  // import.meta.env.DEV is statically false in production, so this branch and
  // its dynamic import are dead-code-eliminated from the prod bundle.
  if (import.meta.env.DEV && window.location.search.includes('curated')) {
    return (
      <Suspense fallback={null}>
        <CuratedEvaluation />
      </Suspense>
    );
  }

  // Early return for dashboard view
  if (currentView === 'dashboard') {
    // Show landing page for unauthenticated users
    if (!user) {
      return (
        <>
          <LandingPage onSignInClick={() => setShowAuthModal(true)} />
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
          />
          <ToastContainer />
          <ConfirmationDialog />
        </>
      );
    }

    // Show dashboard for authenticated users
    return (
      <>
        <Dashboard />
        {/* Keep modals available */}
        <Suspense fallback={null}>
          <SettingsModal
            isOpen={isSettingsModalOpen}
            onClose={() => useModalStore.getState().closeModal('settings')}
            onExportMusicXML={handleExportMusicXML}
            canExport={chords.length > 0}
          />
          <SaveProgressionDialog
            progression={currentProgression}
            isOpen={showSaveDialog}
            onClose={() => setShowSaveDialog(false)}
            onSave={saveProgression}
          />
          <MyProgressionsModal />
          <AnalyzeModal />
        </Suspense>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
        <ToastContainer />
        <ConfirmationDialog />
      </>
    );
  }

  return (
    <div className="app">
      <PatternDefs />
      <header className="app-header">
        <div className="header-left">
          <button
            className="back-to-dashboard"
            onClick={handleBackToDashboard}
            title="Back to Dashboard"
          >
            ←
          </button>
          <h1>N<i>eume</i></h1>
        </div>
        <input
          type="text"
          className="piece-title-input"
          value={pieceTitle}
          onChange={(e) => setPieceTitle(e.target.value)}
          placeholder="Untitled"
          aria-label="Piece title"
        />
        <div className="header-right">
          <button
            className="library-button"
            onClick={() => openProgressionsModal()}
            aria-label="My Library"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </button>
          <button
            className="new-piece-button"
            onClick={handleNewPiece}
            aria-label="New piece"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          </button>
          <button
            className="save-button"
            onClick={() => setShowSaveDialog(true)}
            disabled={chords.length === 0}
            aria-label="Save progression"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
          </button>
          <button
            className="settings-button"
            onClick={() => useModalStore.getState().openModal('settings')}
            aria-label="Settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          <UserMenu onSignInClick={() => setShowAuthModal(true)} />
        </div>
      </header>

      <Sidebar>
        {/* Musical Settings */}
        <SidebarSection>
          <HelpTooltip
            content={HELP_CONTENT['key-selector'].content}
            title={HELP_CONTENT['key-selector'].title}
            examples={HELP_CONTENT['key-selector'].examples}
            position="right"
          >
            <KeySelector
              value={currentKey}
              onChange={setCurrentKey}
              mode={currentMode}
            />
          </HelpTooltip>

          <HelpTooltip
            content={HELP_CONTENT['mode-selector'].content}
            title={HELP_CONTENT['mode-selector'].title}
            examples={HELP_CONTENT['mode-selector'].examples}
            position="right"
          >
            <ModeToggle
              value={currentMode}
              onChange={setCurrentMode}
            />
          </HelpTooltip>

          <BeatsSelector
            value={beatsPerMeasure}
            onChange={setBeatsPerMeasure}
          />
        </SidebarSection>

        <SidebarDivider />

        {/* Voicing — placeholder; wired in Wave 4 */}
        <SidebarSection>
          <label style={{ fontSize: '11px', fontFamily: 'var(--font-ui)', color: 'var(--warm-text-secondary)', marginBottom: '4px', display: 'block' }}>Voicing</label>
          <select disabled value="SATB" style={{ width: '100%', fontFamily: 'var(--font-ui)', fontSize: '12px' }}>
            <option value="SATB">SATB</option>
            <option value="SAB">SAB</option>
            <option value="TTBB">TTBB</option>
            <option value="SSAA">SSAA</option>
            <option value="unison">Unison</option>
            <option value="eight">8-voice</option>
          </select>
        </SidebarSection>

        <SidebarDivider />

        {/* Reverb — placeholder; wired in Wave 4 */}
        <SidebarSection>
          <label style={{ fontSize: '11px', fontFamily: 'var(--font-ui)', color: 'var(--warm-text-secondary)', marginBottom: '4px', display: 'block' }}>Reverb</label>
          <select disabled value="cathedral" style={{ width: '100%', fontFamily: 'var(--font-ui)', fontSize: '12px' }}>
            <option value="cathedral">Cathedral</option>
            <option value="chapel">Chapel</option>
            <option value="concertHall">Concert Hall</option>
            <option value="outdoor">Outdoor</option>
            <option value="studio">Studio</option>
            <option value="rehearsal">Rehearsal</option>
          </select>
        </SidebarSection>

        <SidebarDivider />

        {/* Tempo */}
        <SidebarSection className="tempo-section">
          <HelpTooltip
            content={HELP_CONTENT['tempo-dial'].content}
            title={HELP_CONTENT['tempo-dial'].title}
            shortcut={HELP_CONTENT['tempo-dial'].shortcut}
            position="right"
          >
            <PulseRingTempo
              tempo={tempo}
              onTempoChange={setTempo}
              minTempo={60}
              maxTempo={220}
              isPlaying={isPlaying}
            />
          </HelpTooltip>
        </SidebarSection>

        <SidebarDivider />

        {/* Action Buttons */}
        <SidebarSection className="actions-section">
          <HelpTooltip
            content={HELP_CONTENT['play-button'].content}
            title={HELP_CONTENT['play-button'].title}
            shortcut={HELP_CONTENT['play-button'].shortcut}
            position="right"
          >
            <button
              onClick={handlePlayToggle}
              disabled={chords.length === 0}
              className={`action-button play-button ${isPlaying ? 'is-playing' : ''}`}
              aria-label={isPlaying ? 'Stop playback' : 'Play progression'}
            >
              {isPlaying ? (
                <>
                  <svg className="stop-icon" viewBox="0 0 16 16" fill="currentColor" stroke="none">
                    <rect x="3" y="3" width="10" height="10" rx="1" />
                  </svg>
                  <span>Stop</span>
                </>
              ) : (
                <>
                  <svg className="play-icon" viewBox="0 0 16 16" fill="currentColor" stroke="none">
                    <path d="M4 2.5v11l9-5.5-9-5.5z" />
                  </svg>
                  <span>Play</span>
                </>
              )}
            </button>
          </HelpTooltip>

          <button
            type="button"
            className="clips-button"
            onClick={() => useModalStore.getState().openModal('snippets')}
            aria-label="Open clips"
          >
            Clips
          </button>
        </SidebarSection>

        <SidebarSpacer />

        {/* Help at bottom */}
        <Suspense fallback={null}>
          <KeyboardShortcutsGuide
            isOpen={isShortcutsGuideOpen}
            onClose={() => useModalStore.getState().closeModal('keyboardShortcuts')}
          />
        </Suspense>
      </Sidebar>

      <div className="canvas-area">
        {metadata && (
          <MetadataBanner
            title={metadata.title}
            composer={metadata.composer}
            sourceUrl={metadata.sourceUrl}
            onClear={() => {
              clearAnalyzedProgression();
              setChords([]); // Clear the canvas
            }}
            showBuildFromBones={!!analysisResult}
            onBuildFromBones={handleBuildFromBones}
            onLearnThisPiece={
              chords.some((c) => c.source === 'analyzed')
                ? () => useModalStore.getState().openModal('learnPiece')
                : undefined
            }
          />
        )}

        <DroppableCanvas
        chords={chordsWithPlayState}
        phrases={phraseBoundaries || []}
        songContext={metadata ? { title: metadata.title, composer: metadata.composer, sourceUrl: metadata.sourceUrl } : undefined}
        currentKey={currentKey}
        currentMode={currentMode}
        beatsPerMeasure={beatsPerMeasure}
        zoom={zoom}
        isPlaying={isPlaying}
        playheadPosition={playheadPosition}
        totalBeats={totalBeats}
        selectedChordIds={selectedChordIds}
        showVoiceLanes={showVoiceLanes}
        onUpdateChordPosition={handleUpdateChordPosition}
        onUpdateChord={handleUpdateChord}
        onAddChord={handleAddChord}
        onSelectChord={handleSelectChord}
        onSelectChords={handleSelectChords}
        onToggleChordSelection={handleToggleChordSelection}
        onSelectRange={handleSelectRange}
        onClearSelection={handleClearSelection}
        onSelectAll={handleSelectAll}
        onDeleteSelected={handleDeleteSelected}
        onDuplicateSelected={handleDuplicateSelected}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onZoomChange={setZoom}
        onShowHelp={() => useModalStore.getState().openModal('keyboardShortcuts')}
        onTogglePlay={handlePlayToggle}
        onStop={stop}
        onTempoChange={(delta) => setTempo(Math.max(60, Math.min(220, tempo + delta)))}
        onSave={handleSave}
        onAnalyze={openAnalyzeModal}
        onRefine={() => {
          if (selectedChordIds.length > 0) {
            openRefineModal(selectedChordIds, chords, handleUpdateChord);
          }
        }}
        onDeepInsight={handleDeepInsight}
        onBuildFromBones={handleBuildFromBones}
        onSeek={seek}
        onSeekToStart={seekToStart}
        onSeekToEnd={seekToEnd}
        onPlayChord={playChordIsolated}
        onAuditionGhost={handleAuditionGhost}
        onCommitGhost={handleCommitGhost}
        onInsertSnippet={handleInsertSnippet}
      />
      </div>

      <DeleteConfirmation
        isOpen={showDeleteConfirm}
        chordCount={selectedChordIds.length}
        onConfirm={performDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <Suspense fallback={null}>
        <AnalyzeModal />
        <MyProgressionsModal />
        <RefineModal />
        <GenerateModal />
        <SettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => useModalStore.getState().closeModal('settings')}
          onExportMusicXML={handleExportMusicXML}
          canExport={chords.length > 0}
        />
      </Suspense>

      <WhyThisPanel />
      <BuildFromBonesPanel />
      <CompositionToolsPanel
        chords={chords}
        onUpdateChordVoices={handleUpdateChordVoices}
      />
      <CritiquePanel
        onSelectChords={handleSelectChords}
      />
      <HarmonizePanel
        isOpen={harmonizeIsOpen}
        onClose={closeHarmonizePanel}
        onApplyHarmonization={handleApplyHarmonization}
      />
      <MovesPalette
        context={moveContext}
        onApplyMove={handleApplyMove}
        onUndo={handleUndo}
        onHearNew={() => hearRegion('new')}
        onHearOld={() => hearRegion('old')}
      />
      {/* Exactly-1-selected (MovesPalette) vs. at-least-2-selected (clip this)
          share the same top-right dock — they never render at once. */}
      <ClipThisButton count={selectedChordIds.length} onClip={handleClipThis} />
      <SnippetsPanel
        onPlaySnippet={playSelectedChords}
        onAppendSnippet={handleAppendSnippet}
        onVoiceItLike={handleVoiceItLike}
      />
      <LearnPiecePicker
        chords={chords}
        mode={currentMode}
        pieceName={metadata?.title || (pieceTitle !== 'Untitled' ? pieceTitle : 'this piece')}
        composerName={metadata?.composer}
      />
      <ShiftBar
        chords={chords}
        currentKey={currentKey}
        currentMode={currentMode}
        onApplyStep={handleApplyShiftStep}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <AudioLoadingIndicator />

      <Suspense fallback={null}>
        <SaveProgressionDialog
          progression={currentProgression}
          isOpen={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          onSave={saveProgression}
        />
      </Suspense>

      <ToastContainer />
      <ConfirmationDialog />
    </div>
  );
}

export default App;
